import { create } from 'zustand'
import { persist, createJSONStorage, devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import {
  SessionResult,
  SessionStatus,
  InputState,
  InputType,
  LabelTypes,
  Moment,
  PreviewType,
  PopupCloseSetting,
  SubdomainType,
  HTTP_STATUS,
  SessionAction,
  CUSTOMER_PORTAL_STEP_TYPES,
} from '@/constants'
import { authService, getAuthToken, removeAuthToken, setAuthToken } from '@/services'
import { cartService } from '@/services/cartService'
import { claimQueryCache, cleanupQueryClient, onQueryCacheCleared, refreshPortalQueries } from '@/services/queryClient'
import { customerService } from '@/services/customerService'
import type { SessionActionParams, SessionData as AuthSessionData } from '@/services/authService'

// Import UI store type and access function
import { clearImageCache } from '@/utils/imageCache'
import { createMemoryStorage, isInteractivePreviewMode } from '@/utils/previewMode'
import { useUIStore } from './useUIStore'
import { useCartStore } from './useCartStore'
import {
  idleTimerControls,
  loadBackgroundData,
  setLifelineCallback,
  clearLifelineCallback,
  clearPersistedState,
} from './storeUtils'
import { apiClient } from '@/services/api'
import {
  connectWebSocket,
  closeWebSocket,
  clearPendingAction,
  setSessionMessageHandler,
  setSocketErrorHandler,
} from '@/services/websocket'
import { isShopifyEmbed } from '@/utils/embedConfig'
import { initRecaptchaFromCampaign } from '@/utils/recaptcha'
import { initMouseflowFromCampaign, setMouseflowVariable } from '@/utils/mouseflow'
import { setContentWidth } from '@/utils/cssVariables'
import { SessionInput } from '@/types'
import { emitProcessingMessages, clearProcessingMessages } from '@/utils/processing'
import { isPreviewSession } from '@/utils/session'
import { replaceEqualDeep } from '@tanstack/react-query'

// Session-related types
export interface Campaign {
  id: number
  name: string
  slug: string
  processing_gif?: {
    file: string
  }
  switchable_campaigns?: Campaign[]
  style_variables?: Record<string, any>
  style_omp_theme?: Record<string, any>
  style_labels?: Record<string, any>
  style_url?: string
  style?: string
  favicon?: {
    file: string
  }
  logo?: {
    file: string
  }
  header_content?: string
  header_style?: string
  footer_content?: string
  footer_style?: string
}

export interface Customer {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string
  is_verified: boolean
  payment_sources?: PaymentSource[]
  addresses?: Address[]
  ship_city?: string
  ship_state?: string
  ship_zip?: string
}

export interface PaymentSource {
  id: number
  card_type: string
  last_four: string
  exp_month: number
  exp_year: number
  is_default: boolean
}

export interface Address {
  id: number
  first_name: string
  last_name: string
  address1: string
  address2?: string
  city: string
  state: string
  zip: string
  country: string
  is_default: boolean
}

export type SessionData = AuthSessionData & {
  campaign?: any
  result?: string
  status?: number
  is_active?: boolean
  is_verified?: boolean
  is_first_step?: boolean
  is_lifeline_active?: boolean
  show_lifeline_contact?: boolean
  can_go_home?: boolean
  can_undo?: boolean
  has_checkout?: boolean
  is_popup?: boolean
  is_background_session?: boolean
  popup_close_setting?: number
  valid_inputs?: SessionInput[]
  additional_inputs?: SessionInput[]
  side_menu_inputs?: SessionInput[]
  step?: any
  step_id?: string
  step_slug?: string
  step_type?: string
  started?: boolean
  show_order_summary?: boolean
  subscription_status_labels?: Record<string, string>
  hidden_fees_label?: string
  intents?: number[]
  preview_type?: PreviewType
  moment_status?: Moment
  moment_item?: any | null
}

export interface SessionStore {
  // State
  status: string
  token: string
  campaign: Campaign | null
  session: SessionData | null
  popupSession: SessionData | null
  /** Non-null only while an OMP action drawer owns the funnel. */
  portalPin: SessionData | null
  /** True while the recovery walk is passing through intermediate steps. */
  portalResyncing: boolean
  firstLogin: boolean
  inputState: InputState
  currentLanguage: string
  isLanguageReady: boolean
  selectedItems: any[]
  selectedItemIndex: number
  statusItems: any[]
  startIndex: number
  undoCount: number
  isMatchVerifyError: any[]
  stepStack: any[]
  isCPLoading: boolean
  fulfillments: any[]
  subscriptionOnly: boolean
  popupDisplayState: number
  retentionAuthUrl: string | null

  // Computed selectors
  isSessionStarted: () => boolean
  isSessionValid: () => boolean
  isSessionComplete: () => boolean
  mustLogoutSession: () => boolean
  isSessionActive: () => boolean
  isSessionActiveAndReady: () => boolean
  canLogoutSession: () => boolean
  isSessionUserKnown: () => boolean
  isLastItem: () => boolean
  currentSelectedItem: () => any | null
  canGotoPreviousItem: () => boolean
  isMainMenu: () => boolean
  getSubdomainType: () => SubdomainType
  isCustomerPortal: () => boolean
  processingGIF: () => string | null
  isLifelineActive: () => boolean
  showContactForm: () => boolean
  checkoutDisabled: () => boolean
  canShowCloseIcon: () => boolean
  showPopupBackButton: () => boolean
  popupCloseCanUndo: () => boolean
  popupCloseCanSelectFirstInput: () => boolean
  popupCloseCanSelectLastInput: () => boolean
  subscriptionStatusLabels: () => Record<string, any>

  // Actions
  setAuthSuccess: (token: string) => void
  setAuthError: () => void
  setSession: (session: SessionData) => void
  setActionStep: (session: SessionData) => void
  pinPortalSession: (session: SessionData) => void
  releasePortalPin: () => void
  setPortalResyncing: (value: boolean) => void
  setCampaign: (campaign: Campaign) => void
  setInputState: (state: InputState) => void
  setLanguage: (language: string) => void
  setLanguageReady: (ready: boolean) => void
  addToStepStack: (step: any) => void
  popFromStepStack: () => void
  removeStepsFromStepStack: (count: number) => void
  clearStepStack: () => void
  setSelectedItems: (items: any[]) => void
  setSelectedItemIndex: (index: number) => void
  previousSelectedItem: () => void
  nextSelectedItem: () => void
  updateSelectedItem: () => void
  selectItem: (item: any) => void
  clearSelectedItems: () => void
  removeSelectedItem: (item: any) => void
  setFulfillments: (fulfillments: any[]) => void
  loadFulfillments: (item?: any) => Promise<void>
  hideWelcomeMessage: () => void
  setDisplayState: (displayState: boolean) => void
  setStatusItems: (items: any[]) => void
  incrementUndoCount: () => void
  resetUndoCount: () => void
  setMatchVerifyError: (errors: any[]) => void
  setSubscriptionOnly: (val: boolean) => void
  setPopupDisplayState: (val: number) => void
  resetLifeline: () => void
  resetIdleTimer: () => void
  closeLifeline: () => Promise<void>
  noMatch: (message: string, result?: SessionResult) => void
  logout: () => void

  // Async actions (with React Query integration)
  startSession: (searchParam: any) => Promise<SessionData>
  authorizeSession: (params: any) => Promise<SessionData>
  getSession: () => Promise<SessionData>
  sessionAction: (action: SessionActionParams['action'], params?: any) => Promise<SessionData>
  updateSession: (data: any) => Promise<SessionData>
  updateCustomer: (customer: any) => Promise<any>
  setSessionStarted: () => Promise<SessionData>
  goHome: () => Promise<SessionData>
  undo: (count?: number) => Promise<SessionData>
  getCart: () => Promise<void>
}

// Utility function to get persist key
onQueryCacheCleared(clearImageCache)

function getPersistKey() {
  if (typeof window === 'undefined') {
    return 'ivrpath'
  }

  if (isInteractivePreviewMode()) {
    return 'ivrpath-preview'
  }

  const params = new URLSearchParams(window.location.search)
  const isChatbot = params.get('is_chatbot_embed')
  if (isChatbot) {
    return 'ivrpath-chatbot'
  }
  return 'ivrpath'
}

function isPortalStepType(session: any): boolean {
  return CUSTOMER_PORTAL_STEP_TYPES.includes(Number(session?.step_type))
}

export function setupLifelineCallback() {
  setLifelineCallback(async (result: SessionResult, message: string) => {
    const sessionStore = useSessionStore.getState()
    const session = sessionStore.session
    if (!session?.id) {
      return
    }
    // Entering the lifeline would end the preview and log the viewer out.
    if (isPreviewSession(session)) {
      return
    }

    try {
      await sessionStore.updateSession({ result: String(result), message: message || null })
    } catch (err) {
      const ui = useUIStore.getState()
      ui.setForceDisplayLoader(false)
      ui.setLoading(false)
      ui.setLifelineError(err)
    }
  })
}

export const useSessionStore = create<SessionStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        status: '',
        token: '',
        campaign: null,
        session: null,
        popupSession: null,
        portalPin: null,
        portalResyncing: false,
        firstLogin: false,
        inputState: InputState.Unknown,
        currentLanguage: 'en',
        isLanguageReady: false,
        selectedItems: [],
        selectedItemIndex: 0,
        statusItems: [],
        startIndex: 0,
        undoCount: 0,
        isMatchVerifyError: [],
        stepStack: [],
        isCPLoading: false,
        fulfillments: [],
        subscriptionOnly: false,
        popupDisplayState: 0,
        retentionAuthUrl: null,

        // Computed selectors
        isSessionStarted: () => !!get().token,

        isSessionValid: () => {
          const { token, session } = get()
          return !!token && !!session
        },

        isSessionComplete: () => {
          const { session } = get()
          return session?.result === String(SessionResult.Complete)
        },

        mustLogoutSession: () => {
          const { session } = get()
          return !!(
            session &&
            session.result !== undefined &&
            [String(SessionResult.LifelineNoVerify), String(SessionResult.LifelineLocked)].includes(session.result)
          )
        },

        isSessionActive: () => {
          const store = get()
          return !!(store.isSessionValid() && store.session?.is_active)
        },

        isSessionActiveAndReady: () => {
          const store = get()
          const uiStore = useUIStore.getState()
          const { isLoading, isPopupLoading } = uiStore
          return store.isSessionActive() && !isLoading && !isPopupLoading
        },

        canLogoutSession: () => {
          const store = get()
          const { session } = store
          return (
            store.isSessionValid() &&
            session?.result === String(SessionResult.Active) &&
            session?.status !== SessionStatus.Unmatched
          )
        },

        isSessionUserKnown: () => {
          const store = get()
          const { session } = store
          return !!(store.isSessionValid() && session?.customer && session?.is_verified && session?.is_active)
        },

        isMainMenu: () => {
          const store = get()
          const { inputState } = store
          return !!(
            store.isSessionActiveAndReady() &&
            store.session?.is_first_step &&
            (inputState === InputState.Unknown || inputState === InputState.OkToSubmit)
          )
        },

        getSubdomainType: () => {
          const raw = (get().campaign as any)?.subdomain_type
          return (raw != null ? Number(raw) : SubdomainType.LegacySupport) as SubdomainType
        },

        isCustomerPortal: () => {
          const { session, campaign } = get()
          if (Number((campaign as any)?.subdomain_type) === SubdomainType.CustomerPortal) {
            return true
          }
          // The customer portal is identified by step_type (or first
          // input type) being a customer portal InputType — not tied to subdomain.
          if (!session || (session.is_popup && !(session as any).is_background_session)) {
            return false
          }
          const stepType = Number(session.step_type)
          const firstInputType = Number((session.valid_inputs as any[])?.[0]?.type)
          return CUSTOMER_PORTAL_STEP_TYPES.includes(stepType) || CUSTOMER_PORTAL_STEP_TYPES.includes(firstInputType)
        },

        processingGIF: () => {
          const { campaign } = get()
          return campaign?.processing_gif?.file || '/assets/images/processing.gif'
        },

        isLifelineActive: () => {
          const { session } = get()
          return !!(session?.is_lifeline_active && !session?.show_lifeline_contact)
        },

        showContactForm: () => {
          const { criticalError, lifelineError } = useUIStore.getState()
          const { session } = get()
          // No session, nothing to contact support about — a stale criticalError must
          // not open the sheet over the logout screen, where it can't be answered.
          if (!session) {
            return false
          }
          return !!criticalError || !!lifelineError || !!(session.is_active && session.show_lifeline_contact)
        },

        checkoutDisabled: () => {
          const { session } = get()
          return !session || !session.has_checkout || import.meta.env.VITE_CHECKOUT_DISABLED === 'true'
        },

        isLastItem: () => {
          const { selectedItems, selectedItemIndex } = get()
          return selectedItems.length > 0 && selectedItemIndex === selectedItems.length - 1
        },

        currentSelectedItem: () => {
          const { selectedItems, selectedItemIndex } = get()
          if (selectedItems.length > 0 && selectedItemIndex >= 0 && selectedItemIndex < selectedItems.length) {
            return selectedItems[selectedItemIndex]
          }
          return null
        },

        canGotoPreviousItem: () => {
          const { selectedItemIndex, startIndex } = get()
          return selectedItemIndex > startIndex
        },

        canShowCloseIcon: () => {
          const { session, popupSession } = get()
          if (
            session?.is_popup &&
            !(popupSession as any)?.is_background_session &&
            popupSession?.popup_close_setting &&
            popupSession.popup_close_setting !== PopupCloseSetting.NoClose
          ) {
            if (popupSession.popup_close_setting === PopupCloseSetting.Undo) {
              // Step previews have no server undo, so allow closing regardless.
              // Also allow a single-input Undo popup that can go home or is the
              // first step — otherwise the user gets wedged with no "back" to undo.
              return !!(
                popupSession.can_undo ||
                (popupSession as any).is_step_preview ||
                (((popupSession as any).can_go_home || popupSession.is_first_step) &&
                  popupSession.valid_inputs?.length === 1)
              )
            } else if (popupSession.valid_inputs?.length) {
              return true
            }
          }
          return false
        },

        showPopupBackButton: () => {
          const { session, popupSession } = get()
          const undoSettings: number[] = [
            PopupCloseSetting.Undo,
            PopupCloseSetting.UndoOrFirstInput,
            PopupCloseSetting.UndoOrLastInput,
          ]
          return !!(
            session?.is_popup &&
            popupSession?.can_undo &&
            (!popupSession.popup_close_setting || !undoSettings.includes(popupSession.popup_close_setting))
          )
        },

        popupCloseCanUndo: () => {
          const { popupSession } = get()
          const undoSettings: number[] = [
            PopupCloseSetting.Undo,
            PopupCloseSetting.UndoOrFirstInput,
            PopupCloseSetting.UndoOrLastInput,
          ]
          return !!(
            popupSession?.can_undo &&
            popupSession.popup_close_setting &&
            undoSettings.includes(popupSession.popup_close_setting)
          )
        },

        popupCloseCanSelectFirstInput: () => {
          const { popupSession } = get()
          const firstInputSettings: number[] = [PopupCloseSetting.FirstInput, PopupCloseSetting.UndoOrFirstInput]
          return !!(
            popupSession?.valid_inputs?.length &&
            popupSession.popup_close_setting &&
            firstInputSettings.includes(popupSession.popup_close_setting)
          )
        },

        popupCloseCanSelectLastInput: () => {
          const { popupSession } = get()
          const lastInputSettings: number[] = [PopupCloseSetting.LastInput, PopupCloseSetting.UndoOrLastInput]
          return !!(
            popupSession?.valid_inputs?.length &&
            popupSession.popup_close_setting &&
            lastInputSettings.includes(popupSession.popup_close_setting)
          )
        },

        subscriptionStatusLabels: () => {
          const { styleLabels } = useUIStore.getState()
          return (styleLabels || {})[LabelTypes.SubscriptionStatus] || {}
        },

        // Actions
        setAuthSuccess: (token: string) =>
          set(state => {
            state.status = 'success'
            state.token = token
          }),

        setAuthError: () =>
          set(state => {
            state.token = ''
            state.session = null
            state.popupSession = null
            state.portalPin = null
          }),

        setSession: (session: SessionData) => {
          claimQueryCache(session?.id, (session as any)?.customer?.id)

          // Pinned: divert funnel steps so mounted portal pages keep their intents.
          if (get().portalPin) {
            if (!isPortalStepType(session)) {
              get().setActionStep(session)
              return
            }
            set(state => {
              state.portalPin = { ...session }
            })
          }

          // A lifeline step must not replace a live portal step — the pages behind the
          // drawer read intents/moment_item from it and would render empty.
          if (
            !get().portalPin &&
            (session as any).is_lifeline_active &&
            !isPortalStepType(session) &&
            isPortalStepType(get().session)
          ) {
            get().setActionStep(session)
            return
          }

          const prevSession = get().session as any
          const customerJustSet = !prevSession?.customer && !!(session as any).customer
          const prevCampaign = get().campaign
          const nextCampaign = session.campaign ? replaceEqualDeep(prevCampaign, session.campaign) : undefined

          set(state => {
            state.firstLogin = !state.session // only show welcome message at the start

            if (session.is_popup) {
              state.popupSession = { ...session }

              let background: any = state.session ? { ...state.session } : null

              // No prior session means this step is also the entry point — clone
              // the popup as background and mark it hidden so the page behind
              // the modal doesn't render duplicate content.
              if (!background) {
                background = { ...session }
                if (session.is_first_step) {
                  background.is_hidden = true
                }
              }

              background.is_popup = true
              background.is_background_session = true
              const incomingStyles = ((session as any).styles as string[] | undefined) || []
              const existingStyles = (background.styles as string[] | undefined) || []
              background.styles = [...existingStyles, ...incomingStyles]
              background.status = session.status
              background.required_fields = (session as any).required_fields
              background.can_undo = session.can_undo

              // If the popup is targeting the same step the background is on,
              // hide the background so the user only sees the popup content.
              if ((session as any).step_id && background.step_id === (session as any).step_id) {
                background.is_hidden = true
              }

              state.session = background
            } else {
              state.session = { ...session, is_background_session: false }
              state.popupSession = null
            }

            if (session.campaign) {
              const prev = prevCampaign as any
              const next = nextCampaign as any
              const styleChanged =
                !prev ||
                prev.id !== next.id ||
                prev.style_url !== next.style_url ||
                prev.style_variables !== next.style_variables ||
                prev.style_omp_theme !== next.style_omp_theme

              if (nextCampaign !== prevCampaign) {
                state.campaign = nextCampaign as Campaign
              }

              // Update UI store with campaign style variables and branding
              const uiStore = useUIStore.getState()

              if (state.firstLogin || styleChanged) {
                if (session.campaign.style_variables) {
                  uiStore.setStyleVariables(session.campaign.style_variables)
                }

                if (session.campaign.style_labels) {
                  uiStore.setStyleLabels(session.campaign.style_labels)
                }

                if (session.campaign.style_url) {
                  uiStore.setStyleUrl(session.campaign.style_url)
                }

                if (session.campaign.style) {
                  uiStore.setStyle(session.campaign.style)
                }

                if (session.campaign.favicon?.file) {
                  uiStore.setFaviconUrl(session.campaign.favicon.file)
                }

                // Load ReCAPTCHA script if campaign has a site key
                initRecaptchaFromCampaign(session.campaign)
                // Load Mouseflow if enabled
                initMouseflowFromCampaign(session.campaign)

                if (typeof window !== 'undefined') {
                  const processingGif = (session.campaign as any)?.processing_gif?.file
                  if (processingGif) {
                    try {
                      localStorage.setItem('processing_gif', processingGif)
                    } catch {
                      /* localStorage may be unavailable in incognito */
                    }
                  }
                }
              }
            }
          })

          const uiStore = useUIStore.getState()
          const sess = session as any
          if (sess.status_messages?.length && sess.step_type !== InputType.ItemStatus) {
            uiStore.setMessages(sess.status_messages.slice())
          }
          if (sess.warning_messages?.length) {
            uiStore.setError(sess.warning_messages.slice())
          }

          if (sess.content_config) {
            setContentWidth(sess.content_config)
            uiStore.setBannerHidden(!!sess.content_config.banner?.is_hidden)
            uiStore.setLabelHidden(!!sess.content_config.label?.is_hidden)
            uiStore.setContentHidden(!!sess.content_config.content?.is_hidden)
            uiStore.setMediaHidden(!!sess.content_config.is_hidden)
          }

          // Retention modal — sessions can ship a one-time auth URL that lets
          // an external retention widget intercept intent clicks. Initialize
          // once, then SelectItemIntents will gate on `retentionAuthUrl`.
          if (sess.retention_auth_url) {
            set(state => {
              state.retentionAuthUrl = sess.retention_auth_url as string
            })
            const retention = (window as any).SolvpathRetentionLite
            if (retention?.init) {
              try {
                retention.init({
                  closeOnEsc: true,
                  closeOnBackdrop: false,
                  lockBodyScroll: true,
                  callbacks: {
                    onOpen: (payload: any) => console.log('retention modal opened', payload),
                    onClose: (payload: any) => {
                      console.log('retention modal closed', payload)
                      refreshPortalQueries()
                    },
                    onError: (payload: any) => console.error('retention modal error', payload),
                  },
                })
              } catch (e) {
                console.error('SolvpathRetentionLite init failed', e)
              }
            }
          }

          if (sess.id) {
            setMouseflowVariable('session', sess.id)
          }
          if (sess.campaign?.id) {
            setMouseflowVariable('campaign', sess.campaign.id)
          }

          // Cart loading is handled explicitly by CustomerMatch/CustomerVerify
          // after a successful match or verify; firing it here too would just
          // duplicate the request.
          void customerJustSet

          idleTimerControls.start()
        },

        setActionStep: (session: SessionData) => {
          set(state => {
            state.popupSession = { ...session }
            const background = state.session as any
            if (background) {
              background.is_popup = true
              background.is_background_session = true
              // App.tsx loads builder stylesheets from state.session.styles, so a step's
              // own styles must reach the background or its content renders unstyled.
              const incomingStyles = ((session as any).styles as string[] | undefined) || []
              if (incomingStyles.length) {
                const existingStyles = (background.styles as string[] | undefined) || []
                background.styles = [...new Set([...existingStyles, ...incomingStyles])]
              }
              background.status = session.status
              background.is_lifeline_active = (session as any).is_lifeline_active
              background.show_lifeline_contact = (session as any).show_lifeline_contact
              ;(background as any).lifeline_type = (session as any).lifeline_type
            }
          })

          const uiStore = useUIStore.getState()
          const sess = session as any
          if (sess.status_messages?.length && sess.step_type !== InputType.ItemStatus) {
            uiStore.setMessages(sess.status_messages.slice())
          }
          if (sess.warning_messages?.length) {
            uiStore.setError(sess.warning_messages.slice())
          }

          idleTimerControls.reset()
        },

        pinPortalSession: (session: SessionData) =>
          set(state => {
            state.portalPin = { ...session }
          }),

        setPortalResyncing: (value: boolean) =>
          set(state => {
            state.portalResyncing = value
          }),

        releasePortalPin: () =>
          set(state => {
            state.portalPin = null
            state.popupSession = null
            if (state.session) {
              state.session.is_popup = false
              ;(state.session as any).is_background_session = false
            }
          }),

        setCampaign: (campaign: Campaign) =>
          set(state => {
            state.campaign = state.campaign ? { ...state.campaign, ...campaign } : campaign
          }),

        setInputState: (inputState: InputState) =>
          set(state => {
            state.inputState = inputState
          }),

        setLanguage: (language: string) =>
          set(state => {
            state.currentLanguage = language
          }),

        setLanguageReady: (ready: boolean) =>
          set(state => {
            state.isLanguageReady = ready
          }),

        addToStepStack: (step: any) =>
          set(state => {
            state.stepStack.push(step)
          }),

        popFromStepStack: () =>
          set(state => {
            state.stepStack.pop()
            if (state.stepStack.length === 0) {
              state.undoCount = 0
            }
          }),

        removeStepsFromStepStack: (count: number) =>
          set(state => {
            state.stepStack.splice(state.stepStack.length - count, count)
            if (state.stepStack.length === 0) {
              state.undoCount = 0
            }
          }),

        clearStepStack: () =>
          set(state => {
            state.stepStack = []
            state.undoCount = 0
          }),

        setSelectedItems: (items: any[]) =>
          set(state => {
            state.selectedItems = items
          }),

        setSelectedItemIndex: (index: number) =>
          set(state => {
            state.selectedItemIndex = index
          }),

        previousSelectedItem: () =>
          set(state => {
            if (state.selectedItemIndex > state.startIndex) {
              state.selectedItemIndex--
            }
          }),

        nextSelectedItem: () =>
          set(state => {
            if (state.selectedItemIndex < state.selectedItems.length - 1) {
              state.selectedItemIndex++
            }
          }),

        updateSelectedItem: () =>
          set(state => {
            // Update the current selected item if needed
            const currentItem = state.selectedItems[state.selectedItemIndex]
            if (currentItem) {
              // Trigger reactivity update
              state.selectedItems = [...state.selectedItems]
            }
          }),

        selectItem: (item: any) =>
          set(state => {
            const index = state.selectedItems.findIndex(i => i.id === item.id)
            if (index !== -1) {
              state.selectedItemIndex = index
            }
          }),

        clearSelectedItems: () =>
          set(state => {
            state.selectedItems = []
            state.selectedItemIndex = 0
            state.startIndex = 0
          }),

        removeSelectedItem: (item: any) =>
          set(state => {
            const idx = state.selectedItems.findIndex((i: any) => i.id === item?.id)
            if (idx === -1) {
              return
            }
            state.selectedItems.splice(idx, 1)
            if (state.selectedItemIndex >= state.selectedItems.length) {
              state.selectedItemIndex = Math.max(0, state.selectedItems.length - 1)
            }
          }),

        setFulfillments: (fulfillments: any[]) =>
          set(state => {
            state.fulfillments = fulfillments
          }),

        loadFulfillments: async (item?: any) => {
          const session = get().session as any
          const selectedItem = item || session?.selected_item
          const itemId = selectedItem?.id
          if (!itemId) {
            return
          }
          try {
            const response = await apiClient.get(`/order_items/${itemId}/fulfillments/`)
            const data = (response.data?.data ?? response.data) as any[]
            set(state => {
              state.fulfillments = data
              if (state.session) {
                ;(state.session as any).fulfillments = data
              }
            })
          } catch (err) {
            console.error('loadFulfillments failed', err)
          }
        },

        hideWelcomeMessage: () =>
          set(state => {
            state.firstLogin = false
          }),

        setDisplayState: (displayState: boolean) =>
          set(state => {
            if (state.session) {
              state.session.is_popup = !displayState
            }
            // Also update UI store for popup display state
            const uiStore = useUIStore.getState()
            uiStore.setPopupDisplayState(displayState ? { isVisible: true } : null)
          }),

        setStatusItems: (items: any[]) =>
          set(state => {
            state.statusItems = [...items]
          }),

        incrementUndoCount: () => {
          set(state => {
            state.undoCount += 1
          })
          // Check if undo count triggers lifeline
          const { session, undoCount } = get()
          if (
            session &&
            (session as any).can_enter_lifeline &&
            (session as any).lifeline_undo_count &&
            undoCount >= (session as any).lifeline_undo_count
          ) {
            const uiStore = useUIStore.getState()
            const lifelineCallback = uiStore.lifelineCallback
            if (typeof lifelineCallback === 'function') {
              lifelineCallback(SessionResult.LifelineUndo, 'Lifeline triggered by undo count')
            }
          }
        },

        resetUndoCount: () =>
          set(state => {
            state.undoCount = 0
          }),

        setMatchVerifyError: (errors: any[]) =>
          set(state => {
            state.isMatchVerifyError = errors
          }),

        setSubscriptionOnly: (val: boolean) =>
          set(state => {
            state.subscriptionOnly = val
          }),

        setPopupDisplayState: (val: number) =>
          set(state => {
            state.popupDisplayState = val
          }),

        resetLifeline: () => {
          set(state => {
            state.undoCount = 0
            state.status = 'success'
            if (state.session) {
              state.session.is_lifeline_active = false
              state.session.show_lifeline_contact = false
              ;(state.session as any).lifeline_type = null
            }
            const uiStore = useUIStore.getState()
            uiStore.clearError()
            uiStore.setCriticalError(null)
            uiStore.setLifelineError(null)
            // forceDisplayLoader may have been set when the lifeline was triggered
            // (e.g. UndoPage with can_enter_exit_lifeline). Clear it so loaders
            // don't stick after lifeline exits without contact.
            uiStore.setForceDisplayLoader(false)
            // Only the diverted lifeline step — a funnel popup step must survive.
            if ((state.popupSession as any)?.is_lifeline_active) {
              state.popupSession = null
            }
          })
          idleTimerControls.clear()
        },

        resetIdleTimer: () => {
          idleTimerControls.reset()
        },

        closeLifeline: async () => {
          const { session } = get()
          if (!session?.id) {
            return
          }
          const ui = useUIStore.getState()
          ui.setLoading(true)
          try {
            await get().sessionAction(SessionAction.Close, {})
          } catch (err) {
            console.error('Failed to close lifeline:', err)
          } finally {
            get().resetLifeline()
            ui.clearUIMessages()
            ui.setLoading(false)
          }
        },

        noMatch: (message: string, result: SessionResult = SessionResult.LifelineNoMatch) => {
          const ui = useUIStore.getState()
          const cb = ui.lifelineCallback
          if (typeof cb !== 'function') {
            ui.setError([message])
            return
          }
          if ((get().session as any)?.can_enter_unmatched_lifeline) {
            ui.setError([message])
          } else {
            ui.setLoading(false)
          }
          ui.setLifelineError(message)
          cb(result, message)
        },

        logout: () => {
          idleTimerControls.clear()
          clearLifelineCallback()
          removeAuthToken()

          // Tear down the live socket and reject any in-flight action waiting
          // for a push-back so the UI doesn't hang on a never-coming message.
          closeWebSocket()
          clearPendingAction(new Error('User logged out during pending action'))

          // Single source of truth for storage cleanup — every persisted key
          // (zustand slices + per-feature flags) is removed in one place. Runs
          // before the state reset so the subsequent set() re-persists the
          // retained `campaign` branding — the logout screen keeps its
          // header/footer frame across a refresh (session itself stays null).
          clearPersistedState()
          // Server state lives in React Query, so it outlives the slices cleared above.
          cleanupQueryClient()

          set(state => {
            state.status = ''
            state.token = ''
            state.session = null
            state.popupSession = null
            state.portalPin = null
            state.selectedItems = []
            state.selectedItemIndex = 0
            state.startIndex = 0
            state.undoCount = 0
            state.isMatchVerifyError = []
            state.stepStack = []
            state.inputState = 0
            state.retentionAuthUrl = null
          })

          const ui = useUIStore.getState()
          ui.clearMessages()
          ui.clearUIMessages()
          // Clear any critical error too — otherwise showContactForm() stays
          // truthy and the Lifeline contact popup re-opens on the logout screen
          // with no session to send to.
          ui.clearError()
          ui.setWarning(null)
          ui.setForceDisplayLoader(false)
          ui.setInitialLoading(false)
          ui.setPopupLoading(false)
          ui.setCPLoading(false)
          ui.setLoading(false)
          useCartStore.getState().clearCart()
        },

        // Async actions (integrate with our API services and React Query)
        startSession: async (searchParam: any) => {
          // Guarantee any prior user's persisted slices are gone before the new
          // session writes — otherwise a page refresh between logout and login
          // hydrates the wrong cart/FAQ/UI state.
          clearPersistedState()
          cleanupQueryClient()
          useCartStore.getState().clearCart()

          const store = get()

          try {
            const session = await authService.startSession(searchParam)
            const token = getAuthToken() || ''
            store.setAuthSuccess(token)
            store.setSession(session)

            // Pre-populate selected items at boot so downstream code (Session
            // init, OrderTracking, loadFulfillments) has the item from the start.
            if ((session as any)?.selected_item) {
              store.setSelectedItems([(session as any).selected_item])
            }

            idleTimerControls.start()
            loadBackgroundData()
            setupLifelineCallback()
            connectWebSocket().catch(err => console.error('WebSocket connect failed', err))

            return session
          } catch (error) {
            store.setAuthError()
            throw error
          }
        },

        authorizeSession: async (params: any) => {
          const { token: prevToken, session: prevSession } = get()
          clearPersistedState()
          cleanupQueryClient()
          useCartStore.getState().clearCart()
          set(state => {
            state.inputState = InputState.Unknown
          })

          const store = get()

          try {
            let session = await authService.authorizeSession(params)

            // The Shopify page keeps the first session's link, so it can point at a session the user
            // already logged out of (they logged out and started a new one inside the iframe); the
            // backend returns it unchanged. Resume the session the browser is currently using, else
            // start fresh. Shopify embed only: a standalone link opens the session it names.
            if (isShopifyEmbed() && Number(session?.result) === SessionResult.Logout) {
              if (!prevToken || !prevSession?.is_active) {
                removeAuthToken()
                throw new Error('Session already logged out')
              }
              setAuthToken(prevToken)
              try {
                session = await authService.getSession()
              } catch (error) {
                removeAuthToken()
                throw error
              }
            }

            const token = getAuthToken() || ''
            store.setAuthSuccess(token)
            store.setSession(session)

            if ((session as any)?.selected_item) {
              store.setSelectedItems([(session as any).selected_item])
            }

            idleTimerControls.start()
            loadBackgroundData()
            setupLifelineCallback()
            connectWebSocket().catch(err => console.error('WebSocket connect failed', err))

            return session
          } catch (error) {
            store.setAuthError()
            throw error
          }
        },

        getSession: async () => {
          const result = await authService.getSession()
          get().setSession(result)
          return result
        },

        sessionAction: async (action: SessionActionParams['action'], params: any = {}) => {
          const { session } = get()
          if (!session) {
            throw new Error('No active session')
          }

          const result = await authService.sessionAction(session.id, {
            action,
            params,
          })
          get().setSession(result)
          idleTimerControls.reset()
          return result
        },

        updateSession: async (data: any) => {
          const { session } = get()
          if (!session) {
            throw new Error('No active session')
          }

          const result = await authService.updateSession(session.id, data)
          get().setSession(result)
          return result
        },

        updateCustomer: async (customer: any) => {
          const { session } = get()
          if (!session?.customer?.id) {
            throw new Error('No customer on session')
          }

          const result = await customerService.updateCustomer(String(session.customer.id), customer)
          set(state => {
            if (state.session) {
              state.session.customer = { ...state.session.customer, ...(result as any) }
            }
          })
          return result
        },

        setSessionStarted: async () => {
          const { session } = get()
          if (!session) {
            throw new Error('No active session')
          }
          if (session.started) {
            return session
          }
          // The /sessions/{id}/start/ response can omit `id`; applying it via
          // setSession would clobber state.session. Just flip the flag locally.
          await authService.setSessionStarted(session.id)
          set(state => {
            if (state.session) {
              state.session.started = true
            }
          })
          return get().session!
        },

        goHome: async () => {
          const { session } = get()

          if (session && session.can_go_home) {
            useUIStore.getState().setLoading(true)
            try {
              const result = await get().sessionAction(SessionAction.Home, {})
              get().clearStepStack()
              return result
            } finally {
              useUIStore.getState().setLoading(false)
            }
          }
          return session!
        },

        undo: async (count = 1) => {
          useUIStore.getState().setLoading(true)
          emitProcessingMessages()
          try {
            const result = await get().sessionAction(SessionAction.Undo, { count })
            // Unmount the prior step's renderer before the restored item propagates.
            get().setInputState(InputState.Unknown)
            if (count > 1) {
              get().removeStepsFromStepStack(count)
            } else {
              get().popFromStepStack()
            }
            useUIStore.getState().clearMessages()
            get().incrementUndoCount()
            return result
          } finally {
            clearProcessingMessages()
            useUIStore.getState().setLoading(false)
          }
        },

        getCart: async () => {
          try {
            await cartService.getCart()
          } catch (error) {
            console.error('Failed to load cart:', error)
            throw error
          }
        },
      })),
      {
        name: getPersistKey(),
        storage: createJSONStorage(() => (isInteractivePreviewMode() ? createMemoryStorage() : localStorage)),
        // Only persist core session data, not computed values or functions
        partialize: state => ({
          status: state.status,
          token: state.token,
          campaign: state.campaign,
          session: state.session,
          popupSession: state.popupSession,
          firstLogin: state.firstLogin,
          inputState: state.inputState,
          currentLanguage: state.currentLanguage,
          isLanguageReady: state.isLanguageReady,
          selectedItems: state.selectedItems,
          selectedItemIndex: state.selectedItemIndex,
          statusItems: state.statusItems,
          startIndex: state.startIndex,
          undoCount: state.undoCount,
          isMatchVerifyError: state.isMatchVerifyError,
          stepStack: state.stepStack,
          subscriptionOnly: state.subscriptionOnly,
          popupDisplayState: state.popupDisplayState,
        }),
        onRehydrateStorage: () => state => {
          claimQueryCache(state?.session?.id, (state?.session as any)?.customer?.id)
        },
      }
    ),
    { name: 'SessionStore', enabled: import.meta.env.DEV }
  )
)

// Route any session payload pushed over the WebSocket back through setSession
// so the same side-effects (message handling, content_config, mouseflow,
// retention modal init) fire whether the response came via HTTP or push.
setSocketErrorHandler(payload => useUIStore.getState().setError(payload))

setSessionMessageHandler((data: any, status: number) => {
  const store = useSessionStore.getState()
  const ui = useUIStore.getState()
  ui.clearMessages()
  // Server has newer state and is telling us to discard ours. Pre-emptively
  // overwrite the raw session so setSession builds its popup
  // background off the fresh payload instead of the stale one.
  if (status === HTTP_STATUS.RESET_CONTENT && !store.portalPin) {
    useSessionStore.setState(state => {
      state.session = data
    })
  }
  store.setSession(data)
  idleTimerControls.reset()
})

// Selector hooks for common use cases
export const useSessionData = () => useSessionStore(state => state.session)
export const useSessionToken = () => useSessionStore(state => state.token)
export const useCampaign = () => useSessionStore(state => state.campaign)
export const useIsSessionActive = () => useSessionStore(state => state.isSessionActive())
export const useIsSessionValid = () => useSessionStore(state => state.isSessionValid())
export const useCurrentSelectedItem = () => useSessionStore(state => state.currentSelectedItem())
